import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_COORDINATES,
  MAX_FILE_BYTES,
} from '@/api/coordinate-conversion/types'

let rowSeq = 1

export const RESULT_CSV_KEYS = [
  'name',
  'latitude',
  'longitude',
  'utm_east',
  'utm_north',
  'utm_zone',
  'etm_east',
  'etm_north',
  'etm_zone',
  'dms_latitude',
  'dms_longitude',
  'x',
  'y',
  'coordinate_system',
]

const HEADER_ALIASES = {
  name: ['name', 'الاسم', 'point', 'label', 'اسم'],
  latitude: ['latitude', 'lat', 'خط العرض', 'خط_العرض'],
  longitude: ['longitude', 'lng', 'lon', 'long', 'خط الطول', 'خط_الطول'],
  utm_east: ['utm_east', 'easting', 'east', 'شرقي'],
  utm_north: ['utm_north', 'northing', 'north', 'شمالي'],
  utm_zone: ['utm_zone', 'zone', 'منطقة'],
  etm_east: ['etm_east'],
  etm_north: ['etm_north'],
  etm_zone: ['etm_zone'],
  dms_latitude: ['dms_latitude', 'dms_lat'],
  dms_longitude: ['dms_longitude', 'dms_lon', 'dms_lng'],
  x: ['x'],
  y: ['y'],
}

export function createEmptyRow() {
  rowSeq += 1
  return {
    _id: rowSeq,
    name: '',
    latitude: '',
    longitude: '',
    utm_east: '',
    utm_north: '',
    utm_zone: '',
    etm_east: '',
    etm_north: '',
    etm_zone: '',
    dms_latitude: '',
    dms_longitude: '',
    x: '',
    y: '',
  }
}

export function cloneEmptyRows(count = 1) {
  const safeCount = Math.max(1, Math.min(MAX_COORDINATES, Number(count) || 1))
  return Array.from({ length: safeCount }, () => createEmptyRow())
}

export function toOptionalNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const normalized = String(value).trim().replace(/,/g, '')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function toOptionalString(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

export function isValidLatitude(value) {
  const lat = toOptionalNumber(value)
  return lat !== null && lat >= -90 && lat <= 90
}

export function isValidLongitude(value) {
  const lon = toOptionalNumber(value)
  return lon !== null && lon >= -180 && lon <= 180
}

function hasWgs84(row) {
  return isValidLatitude(row.latitude) && isValidLongitude(row.longitude)
}

function hasUtm(row, defaultZone) {
  const east = toOptionalNumber(row.utm_east)
  const north = toOptionalNumber(row.utm_north)
  const zone = toOptionalString(row.utm_zone) || toOptionalString(defaultZone)
  return east !== null && north !== null && Boolean(zone)
}

function hasEtm(row) {
  return toOptionalNumber(row.etm_east) !== null
    && toOptionalNumber(row.etm_north) !== null
    && Boolean(toOptionalString(row.etm_zone))
}

function hasDms(row) {
  return Boolean(toOptionalString(row.dms_latitude) && toOptionalString(row.dms_longitude))
}

function hasXy(row) {
  return toOptionalNumber(row.x) !== null && toOptionalNumber(row.y) !== null
}

export function rowHasCompleteSystem(row, sourceSystem, defaultZone) {
  if (sourceSystem === 'wgs84') return hasWgs84(row)
  if (sourceSystem === 'utm') return hasUtm(row, defaultZone)
  if (sourceSystem === 'etm') return hasEtm(row)
  if (sourceSystem === 'dms') return hasDms(row)
  return hasWgs84(row) || hasUtm(row, defaultZone) || hasEtm(row) || hasDms(row) || hasXy(row)
}

export function isRowBlank(row) {
  return !toOptionalString(row.name)
    && toOptionalNumber(row.latitude) === null
    && toOptionalNumber(row.longitude) === null
    && toOptionalNumber(row.utm_east) === null
    && toOptionalNumber(row.utm_north) === null
    && !toOptionalString(row.utm_zone)
    && toOptionalNumber(row.etm_east) === null
    && toOptionalNumber(row.etm_north) === null
    && !toOptionalString(row.etm_zone)
    && !toOptionalString(row.dms_latitude)
    && !toOptionalString(row.dms_longitude)
    && toOptionalNumber(row.x) === null
    && toOptionalNumber(row.y) === null
}

function appendName(payload, row) {
  const name = toOptionalString(row.name)
  if (name) payload.name = name
}

/**
 * Send only the active system's fields. In auto, send each complete system on the row.
 */
export function buildCoordinatePayload(row, sourceSystem, defaultZone) {
  const payload = {}
  appendName(payload, row)

  if (sourceSystem === 'wgs84') {
    payload.latitude = toOptionalNumber(row.latitude)
    payload.longitude = toOptionalNumber(row.longitude)
    return payload
  }

  if (sourceSystem === 'utm') {
    payload.utm_east = toOptionalNumber(row.utm_east)
    payload.utm_north = toOptionalNumber(row.utm_north)
    const zone = toOptionalString(row.utm_zone)
    if (zone) payload.utm_zone = zone
    return payload
  }

  if (sourceSystem === 'etm') {
    payload.etm_east = toOptionalNumber(row.etm_east)
    payload.etm_north = toOptionalNumber(row.etm_north)
    const zone = toOptionalString(row.etm_zone)
    if (zone) payload.etm_zone = zone
    return payload
  }

  if (sourceSystem === 'dms') {
    payload.dms_latitude = toOptionalString(row.dms_latitude)
    payload.dms_longitude = toOptionalString(row.dms_longitude)
    return payload
  }

  if (hasWgs84(row)) {
    payload.latitude = toOptionalNumber(row.latitude)
    payload.longitude = toOptionalNumber(row.longitude)
  }
  if (toOptionalNumber(row.utm_east) !== null && toOptionalNumber(row.utm_north) !== null) {
    payload.utm_east = toOptionalNumber(row.utm_east)
    payload.utm_north = toOptionalNumber(row.utm_north)
    const zone = toOptionalString(row.utm_zone)
    if (zone) payload.utm_zone = zone
  }
  if (hasEtm(row)) {
    payload.etm_east = toOptionalNumber(row.etm_east)
    payload.etm_north = toOptionalNumber(row.etm_north)
    payload.etm_zone = toOptionalString(row.etm_zone)
  }
  if (hasDms(row)) {
    payload.dms_latitude = toOptionalString(row.dms_latitude)
    payload.dms_longitude = toOptionalString(row.dms_longitude)
  }
  if (hasXy(row)) {
    payload.x = toOptionalNumber(row.x)
    payload.y = toOptionalNumber(row.y)
  }

  return payload
}

export function collectFilledRows(rows) {
  return (rows || []).filter(row => !isRowBlank(row))
}

export function validateCoordinateRows(rows, sourceSystem, defaultZone) {
  const filled = collectFilledRows(rows)
  if (!filled.length) {
    return { ok: false, code: 'empty_rows', invalidIndexes: [] }
  }
  if (filled.length > MAX_COORDINATES) {
    return { ok: false, code: 'too_many', invalidIndexes: [] }
  }

  const invalidIndexes = []
  ;(rows || []).forEach((row, index) => {
    if (isRowBlank(row)) return
    if (!rowHasCompleteSystem(row, sourceSystem, defaultZone)) {
      invalidIndexes.push(index + 1)
    }
  })

  if (invalidIndexes.length) {
    return { ok: false, code: 'incomplete_rows', invalidIndexes }
  }

  return { ok: true, code: null, invalidIndexes: [] }
}

export function buildJsonRequest(rows, sourceSystem, defaultZone) {
  const coordinates = collectFilledRows(rows).map(row => (
    buildCoordinatePayload(row, sourceSystem, defaultZone)
  ))

  const payload = {
    source_system: sourceSystem || 'auto',
    coordinates,
  }

  const zone = toOptionalString(defaultZone)
  if (zone) payload.utm_zone = zone

  return payload
}

export function getFileExtension(file) {
  const name = file && file.name ? String(file.name) : ''
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function validateUploadFile(file) {
  if (!file) return { ok: false, code: 'file_required' }
  const ext = getFileExtension(file)
  if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
    return { ok: false, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, code: 'file_too_large' }
  }
  return { ok: true, code: null }
}

function normalizeHeader(value) {
  return String(value || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
}

function mapHeader(value) {
  const normalized = normalizeHeader(value)
  const keys = Object.keys(HEADER_ALIASES)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (HEADER_ALIASES[key].some(alias => normalizeHeader(alias) === normalized)) {
      return key
    }
  }
  return null
}

function looksLikeHeaderRow(cells) {
  return cells.some(cell => mapHeader(cell))
}

function parseDelimitedLine(line) {
  const text = String(line || '')
  if (!text.includes('"') && !text.includes(',')) {
    return text.split('\t').map(cell => cell.trim())
  }

  const cells = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
    } else if ((char === ',' || char === '\t') && !inQuotes) {
      cells.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  cells.push(current.trim())
  return cells
}

function applyMappedRow(target, mapped) {
  Object.keys(mapped).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      target[key] = mapped[key]
    }
  })
}

function inferHeaderlessRow(cells) {
  const mapped = {}
  const values = cells.filter(cell => cell !== '')
  if (!values.length) return mapped

  if (values.length === 1) {
    mapped.name = values[0]
    return mapped
  }

  const firstIsText = toOptionalNumber(values[0]) === null
  const nums = firstIsText ? values.slice(1) : values
  if (firstIsText) mapped.name = values[0]

  if (nums.length >= 3 && /[A-Za-z]|[نشمالي]/.test(String(nums[2]))) {
    mapped.utm_east = nums[0]
    mapped.utm_north = nums[1]
    mapped.utm_zone = nums[2]
    return mapped
  }

  if (nums.length >= 2) {
    const a = toOptionalNumber(nums[0])
    const b = toOptionalNumber(nums[1])
    if (a !== null && a >= -90 && a <= 90 && b !== null && b >= -180 && b <= 180) {
      mapped.latitude = nums[0]
      mapped.longitude = nums[1]
    } else {
      mapped.x = nums[0]
      mapped.y = nums[1]
    }
  }

  return mapped
}

export function parseClipboardRows(text) {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => line.trimEnd())
    .filter(line => line.trim() !== '')

  if (!lines.length) return []

  const parsedLines = lines.map(parseDelimitedLine)
  const first = parsedLines[0]
  const hasHeaders = looksLikeHeaderRow(first)
  const headers = hasHeaders ? first.map(mapHeader) : null
  const dataLines = hasHeaders ? parsedLines.slice(1) : parsedLines

  return dataLines.slice(0, MAX_COORDINATES).map(cells => {
    const row = createEmptyRow()
    if (headers) {
      const mapped = {}
      headers.forEach((key, index) => {
        if (key) mapped[key] = cells[index] || ''
      })
      applyMappedRow(row, mapped)
    } else {
      applyMappedRow(row, inferHeaderlessRow(cells))
    }
    return row
  }).filter(row => !isRowBlank(row))
}

function csvEscape(value) {
  if (value === null || value === undefined) return ''
  const text = String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

export function coordinatesToCsv(rows, keys = RESULT_CSV_KEYS) {
  const lines = [keys.join(',')]
  ;(rows || []).forEach(row => {
    lines.push(keys.map(key => csvEscape(row[key])).join(','))
  })
  return lines.join('\n')
}

export function downloadCsv(filename, csvText) {
  const blob = new Blob([`\uFEFF${csvText}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function formatCoordValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  return value
}

export function wgs84PointsFromConverted(rows) {
  return (rows || []).filter(row => (
    isValidLatitude(row.latitude) && isValidLongitude(row.longitude)
  )).map(row => ({
    name: row.name || '',
    latitude: Number(row.latitude),
    longitude: Number(row.longitude),
  }))
}
